<template>
  <teleport to="head">
    <title>{{ HEAD_TITLE }} | Iniciar Sesión</title>
  </teleport>
  <UnauthNav />
  <div class="lcu-login-page">
    <SecondaryNav />
    <section class="section">
        <header class="lcu-section-header lcu-center-text">
          Iniciar Sesión
        </header>
    </section>
    <div class="lcu-login-box">
      <form v-if="!loading">
        <div class="row row-center">
          <img
            src="/assets/lcu-logo-gold.svg"
            class="lcu-login-logo"
            alt="Legacy Christian University Logo" />
        </div>
        <!-- Standard Login Template -->
        <div class="row" v-if="!firstLogin && !forgotPass && !showResetCode">
          <div class="row row-medium">
            <div class="row row-small">
              <form-field
                :id="'login-email'"
                :label="'Correo Electrónico'"
                :icon="'email'"
                :classes="['lcu-login-field']">
                <input
                  type="email"
                  id="login-email"
                  v-model="validations.email.value"
                  @input="validate(['email']);"
                  :class="{
                    'lcu-login-inputError': validations.email.errors.length > 0
                  }"
                  class="lcu-login-inputText" />
              </form-field>
            </div>
            <div class="row row-small" v-if="validations.email.errors.length > 0">
              <span class="lcu-login-errorMsg">
                {{ validations.email.errors.join(' ') }}
              </span>
            </div>
          </div>
          <div class="row row-medium">
            <div class="row row-small">
              <form-field
                :id="'login-password'"
                :label="'Contraseña'"
                :icon="'lock'"
                :classes="['lcu-login-field']">
                <input
                  :type="showPass ? 'text' : 'password'"
                  id="login-password"
                  v-model="validations.password.value"
                  @input="validate(['password']);"
                  :class="{
                    'lcu-login-inputError': validations.password.errors.length > 0
                  }"
                  class="lcu-login-inputText" />
                <tooltip
                  :text="(showPass ? 'Ocultar' : 'Mostrar') + ' contraseña'"
                  :tag="'button'"
                  name="show-new-password"
                  :on-click="() => { showPass = !showPass; }"
                  :classes="['lcu-icon-eyeSlash', {
                    'lcu-icon-eye': showPass
                  }]">
                </tooltip>
              </form-field>
            </div>
            <div class="row row-small" v-if="validations.password.errors.length > 0">
              <span class="lcu-login-errorMsg">
                {{ validations.password.errors.join(' ') }}
              </span>
            </div>
          </div>
          <div class="row">
            <form-field
              :id="'remember-me'"
              :sideLabel="true"
              :classes="['lcu-login-field']"
              :label="'Recuérdame'">
              <input
                type="checkbox"
                name="remember-me"
                id="remember-me"
                v-model="rememberMe"
                @change="rememberMeCheck"
                class="lcu-form-checkbox" />
            </form-field>
          </div>
          <div class="row" v-if="loginError">
            <span class="lcu-login-errorMsg">
              {{ loginError }}
            </span>
          </div>
          <div class="row">
            <button-cta
              :text="'INICIAR SESIÓN'"
              :classes="['lcu-btn-login', 'lcu-login-arrow']"
              @on-click="onLogin" />
          </div>
          <div class="row">
            <a
              href="#"
              class="lcu-box-link"
              @click.prevent="forgotPass = true">
              ¿Olvidaste tu contraseña?
            </a>
          </div>
        </div>
        <!-- Forgot Password Template -->
        <div class="row" v-if="forgotPass && !firstLogin && !showResetCode">
          <div class="row">
            <div class="row row-small">
              <form-field
                :id="'forgot-pass'"
                :label="resetLabel"
                :classes="['lcu-login-field']">
                <input
                  type="text"
                  id="forgot-pass"
                  v-model="validations.forgot.value"
                  @input="validate(['forgot']);"
                  :class="{
                    'lcu-login-inputError': validations.forgot.errors.length > 0
                  }"
                  class="lcu-login-inputText" />
              </form-field>
            </div>
            <div class="row row-small" v-if="validations.forgot.errors.length > 0">
              <span class="lcu-login-errorMsg">
                {{ validations.forgot.errors.join(' ') }}
              </span>
            </div>
          </div>
          <div class="row" v-if="loginError">
            <span class="lcu-login-errorMsg">
              {{ loginError }}
            </span>
          </div>
          <div class="row">
            <button-cta
              :text="'ENVIAR CÓDIGO'"
              :classes="['lcu-btn-login']"
              @on-click="sendResetCode" />
          </div>
          <div class="row">
            <a
              href="#"
              class="lcu-box-link lcu-backArrow-link"
              @click.prevent="forgotPass = false">
              Volver a iniciar sesión
            </a>
          </div>
        </div>
        <!-- Reset Password / Code Values -->
        <div class="row" v-if="showResetCode">
          <div class="row">
            <div class="row row-small">
              <form-field
                :id="'reset-code'"
                :label="resetCodeLabel"
                :classes="['lcu-login-field']">
                <input
                  type="text"
                  id="reset-code"
                  v-model="validations.forgotCode.value"
                  @input="validate(['forgotCode']);"
                  :class="{
                    'lcu-login-inputError': validations.forgotCode.errors.length > 0
                  }"
                  class="lcu-login-inputText" />
              </form-field>
            </div>
            <div class="row row-small" v-if="validations.forgotCode.errors.length > 0">
              <span class="lcu-login-errorMsg">
                {{ validations.forgotCode.errors.join(' ') }}
              </span>
            </div>
          </div>
          <div class="row">
            <div class="row row-small">
              <form-field
                :id="'reset-pass'"
                :label="'New Password'"
                :classes="['lcu-login-field']"
                :icon="'lock'">
                <input
                  :type="showNewPass ? 'text' : 'password'"
                  id="reset-pass"
                  v-model="validations.newPassword.value"
                  @input="validate(['newPassword']); checkConfirm(); showHint();"
                  :class="{
                    'lcu-login-inputError': newPassError || showCriteriaError,
                  }"
                  class="lcu-login-inputText" />
                <tooltip
                  :text="(showNewPass ? 'Ocultar' : 'Mostrar') + ' contraseña'"
                  :tag="'button'"
                  name="show-new-password"
                  :on-click="() => { showNewPass = !showNewPass; }"
                  :classes="['lcu-icon-eyeSlash', {
                    'lcu-icon-eye': showNewPass
                  }]">
                </tooltip>
              </form-field>
            </div>
            <div class="row row-small" v-if="newPassError || showCriteriaError">
              <span class="lcu-login-errorMsg">
                {{ validations.newPassword.errors.join(' ') }}
                <span v-if="confirmError"> {{ confirmError }} </span>
                <span v-if="showCriteriaError"> {{ formValidationMessages.passwordCriteria }} </span>
              </span>
            </div>
            <div class="row row-small" v-if="showHintGroup">
              <div class="lcu-passHint-group">
                <span
                  class="lcu-hintPass"
                  :class="{ 'lcu-hintPass-good': hint.lc }">
                  {{ passwordHintMessages.lc }}
                </span><br/>
                <span
                  class="lcu-hintPass"
                  :class="{ 'lcu-hintPass-good': hint.uc }">
                  {{ passwordHintMessages.uc }}
                </span><br/>
                <span
                  class="lcu-hintPass"
                  :class="{ 'lcu-hintPass-good': hint.num }">
                  {{ passwordHintMessages.num }}
                </span><br/>
                <span
                  class="lcu-hintPass"
                  :class="{ 'lcu-hintPass-good': hint.sym }">
                  {{ passwordHintMessages.sym }}
                </span><br/>
                <span
                  class="lcu-hintPass"
                  :class="{ 'lcu-hintPass-good': hint.len }">
                  {{ passwordHintMessages.len }}
                </span><br/>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="row row-small">
              <form-field
                :id="'confirm-pass'"
                :label="'Confirm New Password'"
                :icon="'lock'"
                :classes="['lcu-login-field']">
                <input
                  :type="showConfirmPass ? 'text' : 'password'"
                  id="confirm-pass"
                  v-model="validations.confirmPass.value"
                  @input="validate(['confirmPass']); checkConfirm();"
                  :class="{
                    'lcu-login-inputError': confirmPassError,
                  }"
                  class="lcu-login-inputText" />
                <tooltip
                  :text="(showConfirmPass ? 'Ocultar' : 'Mostrar') + ' contraseña'"
                  :tag="'button'"
                  name="show-confirm-password"
                  :on-click="() => { showConfirmPass = !showConfirmPass; }"
                  :classes="['lcu-icon-eyeSlash', {
                    'lcu-icon-eye': showConfirmPass
                  }]">
                </tooltip>
              </form-field>
            </div>
            <div class="row row-small" v-if="confirmPassError">
              <span class="lcu-login-errorMsg">
                {{ validations.confirmPass.errors.join(' ') }}
                <span v-if="confirmError"> {{ confirmError }}</span>
              </span>
            </div>
          </div>
          <div class="row" v-if="loginError">
            <span class="lcu-login-errorMsg">
              {{ loginError }}
            </span>
          </div>
          <div class="row">
            <button-cta
              :text="'RESET PASSWORD'"
              :classes="['lcu-btn-login']"
              @on-click="resetPassword" />
          </div>
          <div class="row">
            <a
              href="#"
              class="lcu-box-link lcu-backArrow-link"
              @click.prevent="forgotPass = false; showResetCode = false;">
              Back to Login
            </a>
          </div>
        </div>
        <!-- First Time Login Template -->
         <!-- TODO: If user never finished confirming signup,
          allow for a login and first time code -->
        <div class="row" v-if="firstLogin && !forgotPass && !showResetCode">
          <div class="row">
            <div class="row row-small">
              <form-field
                :id="'first-login'"
                :label="'Enter the code sent to your email'"
                :classes="['lcu-login-field']">
                <input
                  type="text"
                  id="first-login"
                  v-model="validations.code.value"
                  @input="validate(['code']);"
                  :class="{
                    'lcu-login-inputError': validations.code.errors.length > 0
                  }"
                  class="lcu-login-inputText" />
              </form-field>
            </div>
            <div class="row row-small" v-if="validations.code.errors.length > 0">
              <span class="lcu-login-errorMsg">
                {{ validations.code.errors.join(' ') }}
              </span>
            </div>
          </div>
          <div class="row" v-if="loginError">
            <span class="lcu-login-errorMsg">
              {{ loginError }}
            </span>
          </div>
          <div class="row">
            <button-cta
              :text="'SUBMIT & LOGIN'"
              :classes="['lcu-btn-login']"
              @on-click="onFirstLogin" />
          </div>
        </div>
      </form>
      <div v-else>
        <div class="row row-center">
          <SpinLoader :size="'100px'" />
        </div>
      </div>
    </div>
    <div class="row" v-if="!loading">
      <span class="lcu-login-footer">
        ¿Aún no eres estudiante?
        <router-link
          class="lcu-login-link"
          to="/inscribirse">
          Inscribete ahora.
        </router-link>
        <p>
          <a
            href="#"
            class="lcu-powered-link"
            rel="noopener noreferrer"
            target="_blank">
            Powered by Legacy Christian University
          </a>
        </p>
      </span>
    </div>
  </div>
  <Footer />
</template>

<script>
import Footer from 'ç/footer/Footer.vue';
import FormField from 'ç/forms/inputs/Field.vue';
import SpinLoader from 'ç/loaders/spin/Spin.vue';
import ButtonCta from 'ç/buttons/Cta.vue';
import FormValidator from '@/mixins/Validator.vue';
import passwordCriteria from '@/mixins/PasswordCriteria.vue';
import Tooltip from 'ç/tooltip/Tooltip.vue';
import Auth from '@/mixins/Auth.vue';
import UnauthNav from 'ç/nav/UnauthNav.vue';
import SecondaryNav from 'ç/nav/SecondaryNav.vue';

export default {
  name: 'login-page',
  mixins: [Auth, FormValidator, passwordCriteria],
  computed: {
    newPassError() {
      return this.validations.newPassword.errors.length > 0 || this.confirmError;
    },
    confirmPassError() {
      return this.validations.confirmPass.errors.length > 0 || this.confirmError;
    },
    showCriteriaError() {
      const input = this.validations.newPassword.value.replace(/\s+/g, '');
      return !this.criteriaPass && input !== '';
    },
    resetLabel() {
      // return `Enter the email associated with your
      //   account and we will send a code to reset your password.`;
      return `Ingresa el correo electrónico asociado a tu cuenta y te
        enviaremos un código para restablecer tu contraseña.`;
    },
    resetCodeLabel() {
      // return `Enter the reset password code sent to you and then
      // your new password.`;
      return `Ingresa el código de restablecimiento de contraseña que
        te enviamos y luego tu nueva contraseña.`;
    },
  },
  components: {
    Footer,
    SpinLoader,
    ButtonCta,
    Tooltip,
    UnauthNav,
    SecondaryNav,
    'form-field': FormField,
  },
  data() {
    return {
      loading: false,
      showPass: false,
      showNewPass: false,
      showConfirmPass: false,
      firstLogin: false,
      forgotPass: false,
      loginError: false,
      rememberMe: false,
      showResetCode: false,
      confirmError: false,
      validations: {
        email: {
          value: '',
          required: true,
          errors: [],
        },
        password: {
          value: '',
          required: true,
          errors: [],
        },
        code: {
          value: '',
          required: true,
          errors: [],
        },
        forgotCode: {
          value: '',
          required: true,
          errors: [],
        },
        newPassword: {
          value: '',
          required: true,
          errors: [],
        },
        confirmPass: {
          value: '',
          required: true,
          errors: [],
        },
        forgot: {
          value: '',
          required: true,
          errors: [],
        },
      },
    };
  },
  created() {
    this.rememberMe = JSON.parse(window.localStorage.getItem('rememberMe'));
    const user = JSON.parse(window.localStorage.getItem('lcuCreds'));
    if (this.rememberMe && user) {
      this.validations.email.value = user.username;
      this.validations.password.value = user.password;
    }
    this.checkSession();
  },
  methods: {
    async checkSession() {
      try {
        this.loading = true;
        const valid = await this.$validSession();
        if (valid) {
          this.$router.push('/panel-de-control');
        }
      } catch (e) {
        const message = e.message || e;
        this.$log.error(message);
      } finally {
        this.loading = false;
      }
    },
    rememberMeCheck() {
      window.localStorage.setItem('rememberMe', this.rememberMe);
    },
    async onLogin() {
      try {
        this.loginError = false;
        this.loading = true;
        const validation = this.validate(['email', 'password']);
        if (!validation.passed) {
          return;
        }
        const { password, email } = this.validations;
        await this.$cognitoLogin({
          user: email.value,
          pass: password.value,
        });
        if (this.rememberMe) {
          window.localStorage.setItem('lcuCreds', JSON.stringify({
            username: email.value,
            password: password.value,
          }));
        } else {
          localStorage.removeItem('lcuCreds');
        }
        this.$router.push({ path: '/panel-de-control' });
      } catch (e) {
        if (e && e.code === 'UserNotConfirmedException') {
          this.firstLogin = true;
        } else {
          const message = e.message || e;
          this.loginError = message;
          this.$log.error(message);
        }
      } finally {
        this.loading = false;
      }
    },
    async onFirstLogin() {
      const validation = this.validate(['code']);
      if (!validation.passed) {
        return;
      }
      this.loginError = false;
      this.loading = true;
      try {
        const { code, password, email } = this.validations;
        await this.$confirmNewUser({
          code: code.value,
          user: email.value,
          pass: password.value,
        });
        // this.$emit('load-user');
        this.$router.push({ path: '/panel-de-control' });
      } catch (e) {
        const message = e.message || e;
        this.$log.error(message);
        // if user tries to login for the first time after
        // the 24 hour code expiration, we send a new code
        if (e.code === 'ExpiredCodeException') {
          this.loginError = 'Ese código ya no es válido. Hemos enviado uno nuevo. Por favor, intente de nuevo con el código nuevo.';
          const { email } = this.validations;
          await this.$resendNewUserCode({ email: email.value });
        } else {
          this.loginError = message;
        }
      } finally {
        this.loading = false;
      }
    },
    async sendResetCode() {
      const validation = this.validate(['forgot']);
      if (!validation.passed) {
        return;
      }
      try {
        this.loading = true;
        this.loginError = false;
        this.showResetCode = false;
        const { forgot } = this.validations;
        await this.$forgotPassword({
          user: forgot.value,
        });
        this.showResetCode = true;
      } catch (e) {
        const message = e.message || e;
        this.$log.error(message);
        this.loginError = message;
      } finally {
        this.loading = false;
      }
    },
    async resetPassword() {
      const validation = this.validate([
        'forgotCode',
        'newPassword',
        'confirmPass',
      ]);
      if (!validation.passed) {
        return;
      }
      if (!this.criteriaPass) {
        return;
      }
      if (this.confirmError) {
        return;
      }
      this.loading = true;
      this.loginError = false;
      const {
        forgot,
        forgotCode,
        newPassword,
      } = this.validations;
      try {
        await this.$submitResetPass({
          code: forgotCode.value,
          pass: newPassword.value,
          user: forgot.value,
        });
        // after password is reset, reauthenticate them by logging in
        const user = await this.$cognitoLogin({
          user: forgot.value,
          pass: newPassword.value,
        });
        this.$log.info('Logged in user: ', user);
        // route back to home to grab all the users data
        this.$router.push({ path: '/panel-de-control' });
      } catch (e) {
        this.$log.error('error: ', e);
        this.loginError = e.message || e;
      } finally {
        this.loading = false;
      }
    },
    checkConfirm() {
      this.validations.newPassword.value = this.validations.newPassword.value.replace(/\s+/g, '');
      if (this.validations.confirmPass.value.trim() !== '') {
        this.checkPass();
      }
    },
    checkPass() {
      const { confirmPass, newPassword } = this.validations;
      if (newPassword.value === confirmPass.value) {
        this.confirmError = false;
      } else {
        this.confirmError = this.formValidationMessages.passwordMatch;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.lcu-login-page {
  background-color: #eaeaea;
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 0 0 $public-nav-height;

  .lcu-secondary-nav {
    margin: $public-nav-height 0;
  }

  @media only screen and (max-width: $mobile-max) {

    .lcu-secondary-nav {
      margin: 0 0 50px;
    }
  }
}

.lcu-login-logo {
  width: 75px;
}

.lcu-login-box {
  background-color: $white;
  border-radius: 8px;
  padding: 25px;
  border: 1px solid $page-border;
  width: 90%;

  @media screen and (min-width: $desktop-min) {
    width: 700px;
  }
}

.lcu-btn-login {
  padding: 12px;
}

.lcu-login-arrow {
  &:after {
    font-family: 'lcu';
    content: '\e80a';
    display: inline-block;
    margin-left: 5px;
  }
}

.lcu-login-inputError {
  background-color: #ffdddd;
  border-color: $danger;
}

.lcu-login-errorMsg {
  color: $danger;
}

.lcu-icon-eyeSlash {
  background-color: transparent;
  border: 0;
  bottom: 7px;
  color: rgba($legacy-primary, .5);
  display: inline-block;
  font-size: 21px;
  position: absolute;
  right: 3px;
  text-align: center;

  @media only screen and (max-width: $mobile-max) {
    right: 0;
  }

  &:hover {
    cursor: pointer;
  }

  &:before {
    content: '\e806';
    font-family: 'lcu';
  }
}

.lcu-icon-eye {

  &:before {
    content: '\e81c';
  }
}

.lcu-login-footer {
  color: rgba($black, .6);
  padding-top: 50px;
  text-align: center;
}

.lcu-login-link {
  color: $legacy-primary;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.lcu-powered-link {
  color: $legacy-primary;
  font-size: 13px;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.lcu-box-link {
  color: $legacy-primary;
  font-size: 14px;
  text-align: center;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.lcu-backArrow-link {

  &:before {
    display: inline-block;
    content: '\e81f';
    font-family: 'lcu';
    margin-right: 0;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
    transform-origin: center;
  }
}

.lcu-logo-loading {
  animation: spin 2s linear infinite;
}

.lcu-passHint-group {
  background-color: rgba($black, .05);
  color: $black;
}
</style>
